import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery, useGetZohoEmailSetupMutation } from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { numberScheme, stringScheme } from 'src/utils/validation-schemes';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React, { useEffect } from 'react';
import { EditZohoEmailAutoSetupSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoEmailIntegrationSettings/EditZohoEmailAutoSetupSettings/EditZohoEmailAutoSetupSettings';
import { EditAutocloseAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditAutocloseAdvancedSettings/EditAutocloseAdvancedSettings';
import { EditZohoEmailApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZohoEmailIntegrationSettings/EditZohoEmailApiDetailsSettings/EditZohoEmailApiDetailsSettings';
import { Skeleton } from 'src/components/ui/skeleton';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';

const LoadingBlock = () => (
  <div className={'w-full flex flex-col gap-2.5 p-2.5 rounded-sm-5'}>
    <Skeleton className={'w-full h-12'} />
    <Skeleton className={'w-full h-12'} />
    <Skeleton className={'w-full h-12'} />
  </div>
);

interface ZohoEmailSettingsFormValues extends AutocloseFormValues {
  name: string;
  client_id: string;
  client_secret: string;
  scope: string;
  server_locale: string;
  integration_method: 'code' | 'token';
  code: string;
  refresh_token: string;
}

export const EditZohoEmailIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);
  const [code, setCode] = React.useState('');
  const [getZohoEmailSetup, { data: setupResponse, isLoading: isLoadingZohoEmailSetup }] =
    useGetZohoEmailSetupMutation();

  useEffect(() => {
    if (id && integration.id) {
      getZohoEmailSetup({
        projectId: id,
        integrationId: integration.id,
      });
    }
  }, [id, integration]);

  useEffect(() => {
    if (setupResponse?.custom_function_code) {
      setCode(setupResponse.custom_function_code);
    }
  }, [setupResponse]);

  const methods = useForm<ZohoEmailSettingsFormValues>({
    defaultValues: {
      name: integration?.integration_data?.name,
      client_id: integration?.integration_data?.client_id,
      client_secret: integration?.integration_data?.client_secret,
      scope: integration?.integration_data?.scope || 'Desk.tickets.ALL,Desk.basic.READ',
      server_locale: integration?.integration_data?.server_locale,
      integration_method: integration?.integration_data?.token ? 'token' : 'code',
      code: integration?.integration_data?.code,
      refresh_token: integration?.integration_data?.refresh_token,
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
    },
    resolver: formResolver(
      object({
        name: stringScheme({ required: false }).label('Integration Name'),
        client_id: stringScheme({ required: false }).label('Client ID'),
        client_secret: stringScheme({ required: false }).label('Client secret'),
        scope: stringScheme({ required: false }).label('Scope'),
        server_locale: stringScheme({ required: false }).label('Server locale'),
        code: stringScheme({ required: false }).label('Code'),
        refresh_token: stringScheme({ required: false }).label('Refresh Token'),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
      }),
    ),
  });

  const submit = async (data: ZohoEmailSettingsFormValues) => {
    const result = {
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        name: currentProject.name,
        client_id: data.client_id,
        client_secret: data.client_secret,
        scope: data.scope,
        server_locale: data.server_locale,
        code: data.code,
        refresh_token: data.refresh_token,
      },
    };

    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;

    if (data.integration_method === 'code') {
      delete result.integration_data.refresh_token;
    } else {
      delete result.integration_data.code;
    }

    if (!result.integration_data.code) {
      delete result.integration_data.code;
    }

    if (!result.integration_data.refresh_token) {
      delete result.integration_data.refresh_token;
    }

    if (!result.integration_data.server_locale) {
      delete result.integration_data.server_locale;
    }

    if (!result.integration_data.client_secret) {
      delete result.integration_data.client_secret;
    }

    onSubmit(result);
  };

  return isLoadingUpdateProject || isLoadingZohoEmailSetup ? (
    <LoadingBlock />
  ) : (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="auto-setup-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Setup settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon
                    open={accordionValue.includes('web-widget-settings')}
                  />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoEmailAutoSetupSettings code={code} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditAutocloseAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZohoEmailApiDetailsSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
